import { useSelector } from "react-redux";

import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types";
import { GraphConfig } from "@intelligems/ig-types";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { dataFormatWrapper } from "@src/features/analytics/metricDetails/statSig/utils";
import type { VariantData } from "@src/features/mobileAnalytics/statSigRenderers/types";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedVariations } from "@src/store/slices/variations";
import { sortByOrder } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

interface UseVariantDataParams {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  selectedMetricId: string;
  selectedConfig: GraphConfig;
}

interface UseVariantDataResult {
  variantData: VariantData[];
  selectedBaselineId: string;
}

export const useVariantData = ({
  variationOverviewData,
  selectedMetricId,
  selectedConfig,
}: UseVariantDataParams): UseVariantDataResult => {
  const variations = useSelector(selectedVariations);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const selectedBaseline = variations.find(
    (v) => v.id === dashboardFilters.compareGroupA
  );
  const selectedBaselineId = selectedBaseline?.id ?? "";

  const currency = storeCurrencyConfig?.value || {
    code: "USD",
    locale: "en-US",
  };

  if (variationOverviewData && variationOverviewData[0]?.data?.metrics) {
    const formattedData = variationOverviewData[0].data.metrics.map((data) => {
      const variation = variations.find((v) => v.id === data.variation_id);
      const metricData = data[selectedMetricId];

      return {
        variation_id: data.variation_id,
        name: variation?.name || "Unknown",
        p2bb: metricData?.p2bb,
        p2bc: metricData?.p2bc,
        order: variation?.order ?? 0,
        value: dataFormatWrapper(
          metricData?.value || 0,
          selectedConfig.dataTypes[0],
          currency
        ),
        plus_minus: metricData?.plus_minus,
        uplift: {
          ci_range:
            !metricData?.uplift?.ci_low || !metricData?.uplift?.ci_high
              ? undefined
              : [metricData.uplift.ci_low, metricData.uplift.ci_high],
          value: metricData?.uplift?.value ?? 0,
          plus_minus: metricData?.uplift?.plus_minus,
        },
        ci_range:
          !metricData?.ci_low || !metricData?.ci_high
            ? undefined
            : [
                dataFormatWrapper(
                  metricData.ci_low,
                  selectedConfig.dataTypes[0],
                  currency
                ),
                dataFormatWrapper(
                  metricData.ci_high,
                  selectedConfig.dataTypes[0],
                  currency
                ),
              ],
      };
    });

    // Add variations with no data
    variations.forEach((variation) => {
      if (!formattedData.some((data) => data.variation_id === variation.id)) {
        formattedData.push({
          variation_id: variation.id,
          name: variation.name,
          p2bb: null,
          p2bc: null,
          order: variation.order,
          value: 0,
          plus_minus: null,
          uplift: {
            ci_range: undefined,
            value: 0,
            plus_minus: null,
          },
          ci_range: undefined,
        });
      }
    });

    return {
      variantData: formattedData.sort(sortByOrder),
      selectedBaselineId,
    };
  }

  // Return skeleton data if loading
  const variantData = variations
    .map((variation) => ({
      variation_id: variation.id,
      name: variation.name,
      p2bb: 0,
      p2bc: 0,
      order: variation.order,
      value: 0,
      plus_minus: null,
      uplift: {
        ci_range: undefined,
        value: 0,
        plus_minus: null,
      },
      ci_range: undefined,
    }))
    .sort(sortByOrder);

  return {
    variantData,
    selectedBaselineId,
  };
};
